import { cva, type VariantProps } from 'class-variance-authority';
import type { IconName } from 'lucide-react/dynamic';
import { AnimatePresence, motion } from 'motion/react';
import { Slot } from 'radix-ui';
import { forwardRef } from 'react';

import { cn } from '@/lib/utils';
import { Icon } from './icon';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[10px] text-[13px] font-semibold transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*="size-"])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-none hover:brightness-108',
        destructive:
          'bg-destructive-soft text-destructive border border-destructive/30 hover:bg-destructive/15 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
        outline: 'border border-border-strong bg-transparent hover:bg-muted',
        primary: 'bg-primary text-primary-foreground hover:brightness-108',
        secondary:
          'bg-transparent border border-border-strong text-foreground-2 hover:bg-muted',
        ghost: 'hover:bg-muted text-foreground-2',
        link: 'text-primary underline-offset-4 hover:underline',
        success:
          'bg-success-soft text-success border border-success/30 hover:bg-success/15',
        warning:
          'bg-warning-soft text-warning border border-warning/30 hover:bg-warning/15',
        error:
          'bg-destructive-soft text-destructive border border-destructive/30 hover:bg-destructive/15',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3',
        md: 'h-10 px-6',
        lg: 'h-11 px-8',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    icon?: IconName;
    iconPosition?: 'left' | 'right';
    isLoading?: boolean;
  };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      children,
      icon,
      iconPosition = 'left',
      isLoading = false,
      disabled,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const content = (
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.span
            key="loading"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="flex items-center justify-center"
          >
            <Icon name="loader" className="animate-spin" />
          </motion.span>
        ) : (
          <motion.span
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-2"
          >
            {icon && iconPosition === 'left' && (
              <motion.span
                key={icon}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
                className="flex items-center"
              >
                <Icon name={icon} />
              </motion.span>
            )}

            {children}

            {icon && iconPosition === 'right' && (
              <motion.span
                key={icon}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
                className="flex items-center"
              >
                <Icon name={icon} />
              </motion.span>
            )}
          </motion.span>
        )}
      </AnimatePresence>
    );

    const Comp = asChild ? Slot.Root : motion.button;

    return (
      <Comp
        ref={ref}
        type={type}
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(
          buttonVariants({ variant, size, className }),
          'relative overflow-hidden',
        )}
        disabled={disabled || isLoading}
        whileTap={!asChild ? { scale: 0.96 } : undefined}
        transition={{ duration: 0.15 }}
        {...props}
      >
        <span className="flex items-center justify-center gap-2">{content}</span>
      </Comp>
    );
  },
);
